import { ReplyMarkup, UrlButtonData } from "../interfaces/contents"

export const CONTENT_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,.mov"
export const CONTENT_MAX_FILES = 10
export const CONTENT_IMAGE_GIF_MAX_BYTES = 104857600 // 100 МиБ
export const CONTENT_VIDEO_MAX_BYTES = 209715200 // 200 МиБ
export const CONTENT_TOTAL_MAX_BYTES = 262144000 // 250 МиБ — клиентский запас до серверных 256 МиБ
export const CONTENT_MAX_TEXT_RUNES = 100000
export const REPLY_MARKUP_MAX_PER_ROW = 8
export const REPLY_MARKUP_MAX_TOTAL = 100

export type ContentFileKind = "image" | "gif" | "video"

// Тип по MIME или расширению: пустой File.type сам по себе не повод отказа (§4.3).
export const contentFileKind = (file: File): ContentFileKind | null => {
  const mime = file.type.toLowerCase()
  if (mime === "image/gif") {
    return "gif"
  }
  if (
    mime === "video/mp4" ||
    mime === "video/webm" ||
    mime === "video/quicktime"
  ) {
    return "video"
  }
  if (mime === "image/jpeg" || mime === "image/png" || mime === "image/webp") {
    return "image"
  }
  const name = file.name.toLowerCase()
  if (name.endsWith(".gif")) {
    return "gif"
  }
  if (
    name.endsWith(".mp4") ||
    name.endsWith(".webm") ||
    name.endsWith(".mov")
  ) {
    return "video"
  }
  if (
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".png") ||
    name.endsWith(".webp")
  ) {
    return "image"
  }
  return null
}

export interface ContentDraft {
  text: string
  newFiles: File[]
  reusedAssets: Array<{ id: number; sizeBytes: number }>
  replyMarkup: ReplyMarkup | null
}

export interface ContentDraftErrors {
  text?: string
  files: string[]
  total?: string
  empty?: string
}

export type ContentButtonFieldErrors = Partial<Record<"text" | "url", string>>

const isValidButtonUrl = (value: string): boolean => {
  // WHATWG URL исправляет пробелы/переводы строк и лишние слеши.
  // Проверяем исходную строку; отправляем её без нормализации.
  if (!/^https?:\/\//.test(value) || value.includes("\\")) return false
  if (
    Array.from(value).some(
      (character) =>
        character.charCodeAt(0) <= 0x20 || character.charCodeAt(0) === 0x7f,
    )
  )
    return false
  const authority = value.slice(value.indexOf("://") + 3).split(/[/?#]/, 1)[0]
  if (!authority) return false
  try {
    const parsed = new URL(value)
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      parsed.hostname.length > 0
    )
  } catch {
    return false
  }
}

export const validateContentButtonFields = (
  button: UrlButtonData,
): ContentButtonFieldErrors => {
  const errors: ContentButtonFieldErrors = {}
  const textLength = Array.from(button.text).length
  if (textLength < 1 || textLength > 64)
    errors.text = "Текст кнопки — от 1 до 64 символов"
  const urlLength = Array.from(button.url).length
  if (urlLength < 1 || urlLength > 256) {
    errors.url =
      "Ссылка кнопки — от 1 до 256 символов и должна начинаться с http:// или https://"
  } else if (!isValidButtonUrl(button.url)) {
    errors.url = "Укажите корректную ссылку с http:// или https://"
  }
  return errors
}

// Проверка количества не зависит от валидности полей соседних черновиков.
export const validateReplyMarkupLayout = (
  markup: ReplyMarkup | null,
): string | null => {
  if (markup === null) {
    return null
  }
  const rows = markup.inline_keyboard
  if (rows.length === 0 || rows.some((row) => row.length === 0)) {
    return "Добавьте хотя бы одну кнопку"
  }
  let total = 0
  for (const row of rows) {
    if (row.length > REPLY_MARKUP_MAX_PER_ROW) {
      return "В строке не может быть больше 8 кнопок"
    }
    total += row.length
  }
  if (total > REPLY_MARKUP_MAX_TOTAL) {
    return "Не может быть больше 100 кнопок"
  }
  return null
}

export const validateReplyMarkup = (
  markup: ReplyMarkup | null,
): string | null => {
  const layoutError = validateReplyMarkupLayout(markup)
  if (layoutError || markup === null) return layoutError
  for (const row of markup.inline_keyboard) {
    for (const button of row) {
      const errors = validateContentButtonFields(button)
      if (errors.text || errors.url) return errors.text ?? errors.url ?? null
    }
  }
  return null
}

export const validateContentDraft = (
  draftContent: ContentDraft,
): ContentDraftErrors => {
  const errors: ContentDraftErrors = { files: [] }

  if (Array.from(draftContent.text).length > CONTENT_MAX_TEXT_RUNES) {
    errors.text = "Текст — не более 100 000 символов"
  }
  if (draftContent.text.includes("\u0000")) {
    errors.text = "Текст содержит запрещённые символы"
  }

  // Дедупликация переиспользуемых по asset.id — комбинированный кап считается
  // по УНИКАЛЬНЫМ (FR-13/FR-20).
  const uniqueReused = new Map<number, number>()
  for (const asset of draftContent.reusedAssets) {
    if (!uniqueReused.has(asset.id)) {
      uniqueReused.set(asset.id, asset.sizeBytes)
    }
  }
  const combinedCount = draftContent.newFiles.length + uniqueReused.size
  if (combinedCount > CONTENT_MAX_FILES) {
    errors.files.push("Не более 10 файлов в материале (новые + существующие)")
  }
  for (const file of draftContent.newFiles) {
    if (file.size === 0) {
      errors.files.push(`${file.name} — пустой файл`)
      continue
    }
    const kind = contentFileKind(file)
    if (kind === null) {
      errors.files.push(
        `${file.name} — неподдержанный формат (JPEG, PNG, WebP, GIF, MP4, WebM, MOV)`,
      )
      continue
    }
    const cap =
      kind === "video" ? CONTENT_VIDEO_MAX_BYTES : CONTENT_IMAGE_GIF_MAX_BYTES
    if (file.size > cap) {
      errors.files.push(
        kind === "video"
          ? `${file.name} — видео больше 200 МБ`
          : `${file.name} — изображение/GIF больше 100 МБ`,
      )
    }
  }

  const textBytes = new TextEncoder().encode(draftContent.text).length
  const reusedBytes = Array.from(uniqueReused.values()).reduce(
    (sum, size) => sum + size,
    0,
  )
  const newFilesBytes = draftContent.newFiles.reduce(
    (sum, file) => sum + file.size,
    0,
  )
  if (newFilesBytes + reusedBytes + textBytes > CONTENT_TOTAL_MAX_BYTES) {
    errors.total = "Суммарный размер (файлы и текст) больше 250 МБ"
  }

  const hasAssets = combinedCount > 0
  const hasValidMarkup =
    draftContent.replyMarkup !== null &&
    validateReplyMarkup(draftContent.replyMarkup) === null
  if (draftContent.text.trim().length === 0 && !hasAssets && !hasValidMarkup) {
    // «Хотя бы одно из»: валидные кнопки сами по себе делают материал непустым.
    errors.empty = "Добавьте текст, файл или кнопку"
  }
  return errors
}

export const hasContentDraftErrors = (errors: ContentDraftErrors): boolean =>
  errors.text !== undefined ||
  errors.files.length > 0 ||
  errors.total !== undefined ||
  errors.empty !== undefined
